type RefreshResponse = {
  access?: string;
  refresh?: string;
  token?: {
    access?: string;
    refresh?: string;
  };
  data?: {
    access?: string;
    refresh?: string;
    token?: {
      access?: string;
      refresh?: string;
    };
  };
};

let refreshPromise: Promise<string | null> | null = null;

const getRefreshUrl = () => {
  const baseUrl = process.env.REACT_APP_MAIN_URL || "";
  return baseUrl ? `${baseUrl.replace(/\/+$/, "")}/refresh/` : "refresh/";
};

const extractTokens = (payload: RefreshResponse | null) => {
  const data = payload?.data;
  const tokenData = payload?.token || data?.token || data || payload;

  return {
    accessToken: tokenData?.access || null,
    refreshToken: tokenData?.refresh || null,
  };
};

export const getAccessToken = () => localStorage.getItem("accessToken");

const isTokenExpired = (token: string, bufferSeconds = 60): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + bufferSeconds * 1000;
  } catch {
    return true;
  }
};

export const ensureFreshToken = async (): Promise<string | null> => {
  const token = getAccessToken();
  if (!token) return null;
  if (!isTokenExpired(token)) return token;
  return refreshAccessToken();
};

export const clearAuthTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};

export const refreshAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken) {
    clearAuthTokens();
    return Promise.resolve(null);
  }

  refreshPromise = fetch(getRefreshUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh: refreshToken }),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Refresh failed with status ${response.status}`);
      }

      const payload = (await response.json().catch(() => null)) as RefreshResponse | null;
      const tokens = extractTokens(payload);

      if (!tokens.accessToken) {
        throw new Error("Refresh response does not contain access token");
      }

      localStorage.setItem("accessToken", tokens.accessToken);

      if (tokens.refreshToken) {
        localStorage.setItem("refreshToken", tokens.refreshToken);
      }

      return tokens.accessToken;
    })
    .catch((error) => {
      console.error("Token refresh failed:", error);
      clearAuthTokens();
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

// Proactively refresh token when user returns to the tab
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && getAccessToken()) {
      ensureFreshToken();
    }
  });
}
